import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase/config";

const Registration: React.FC = () => {
  const { currentUser, signUpWithEmail } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [passwordValidation, setPasswordValidation] = useState({
    minLength: false,
    hasLower: false,
    hasUpper: false,
    hasNumber: false
  });

  useEffect(() => {
    if (currentUser) {
      navigate('/dashboard');
    }
  }, [currentUser, navigate]);

  const handleRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (await checkEmail()) {
        setError('A megadott email cím már használatban van.');
        return;
      }

      if (!isValidEmail(email)) {
        setError('Hibás email cím.');
        return;
      }

      if (!isValidPassword(password)) {
        setError('Hibás jelszó. A jelszónak legalább 6 karakter hosszúnak kell lennie, és tartalmaznia kell kisbetűt, nagybetűt és számot.');
        return;
      }

      if (!isValidPasswordConfirm(password, passwordConfirm)) {
        setError('Hibás jelszó megerősítés.');
        return;
      }

      await signUpWithEmail(email, password, name);
    } catch (error) {
      setError('Sikertelen regisztráció. Kérjük, próbáld újra később.');
    } finally {
      setLoading(false);
    }
  }

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  const isValidPassword = (password: string) => {
    return password.length >= 6 &&
      /[a-z]/.test(password) &&
      /[A-Z]/.test(password) &&
      /[0-9]/.test(password);
  }

  const isValidPasswordConfirm = (password: string, passwordConfirm: string) => {
    return password === passwordConfirm;
  }

  const checkEmail = async () => {
    const usersRef = collection(db, 'users');
    const emailQuery = query(usersRef, where('email', '==', email));
    const querySnapshot = await getDocs(emailQuery);
    return !querySnapshot.empty;
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    setPasswordValidation({
      minLength: value.length >= 6,
      hasLower: /[a-z]/.test(value),
      hasUpper: /[A-Z]/.test(value),
      hasNumber: /[0-9]/.test(value)
    });
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center relative px-4 sm:px-6 lg:px-8 overflow-hidden">
      {/* Dinamikus háttér */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-red-600 via-red-800 to-gray-900"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxkZWZzPjxwYXR0ZXJuIGlkPSJwYXR0ZXJuIiB4PSIwIiB5PSIwIiB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiIHBhdHRlcm5UcmFuc2Zvcm09InJvdGF0ZSgzMCkiPjxyZWN0IHg9IjAiIHk9IjAiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyIiBmaWxsPSJyZ2JhKDI1NSwgMjU1LCAyNTUsIDAuMDUpIj48L3JlY3Q+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB4PSIwIiB5PSIwIiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI3BhdHRlcm4pIj48L3JlY3Q+PC9zdmc+')] opacity-20"></div>
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black opacity-30"></div>

        {/* Animált versenypálya vonalak */}
        <div className="absolute left-0 right-0 bottom-0 h-8 bg-[repeating-linear-gradient(90deg,rgba(255,255,255,0.1),rgba(255,255,255,0.1)_20px,transparent_20px,transparent_40px)] animate-[slideRight_20s_linear_infinite]"></div>
        <div className="absolute left-0 right-0 bottom-12 h-8 bg-[repeating-linear-gradient(90deg,rgba(255,255,255,0.05),rgba(255,255,255,0.05)_30px,transparent_30px,transparent_60px)] animate-[slideRight_30s_linear_infinite]"></div>
      </div>

      <div className="max-w-md w-full p-8 bg-white bg-opacity-95 backdrop-blur-sm rounded-xl shadow-2xl z-10">
        <div className="flex flex-col items-center mb-8">
          <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mb-4 shadow-lg">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-800">SHS Gokart</h1>
          <p className="mt-2 text-center text-gray-600">
            Hozd létre új fiókodat
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-800 rounded-md text-center">
            <div className="flex items-center justify-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span>{error}</span>
            </div>
          </div>
        )}

        <form onSubmit={handleRegistration} className="space-y-4 mb-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Teljes név
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="Kiss János"
              required
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email cím
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="pelda@email.com"
              required
            />
          </div>

          <div className="relative">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Jelszó
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={handlePasswordChange}
              onFocus={(e) => e.currentTarget.parentElement?.querySelector('.password-requirements')?.classList.remove('hidden')}
              onBlur={(e) => e.currentTarget.parentElement?.querySelector('.password-requirements')?.classList.add('hidden')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="••••••••"
              required
            />
            <div className="password-requirements hidden absolute left-0 mt-2 p-4 bg-white border border-gray-200 rounded-md shadow-lg z-10 w-72">
              <p className="text-sm font-medium text-gray-700 mb-2">A jelszónak tartalmaznia kell:</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li className="flex items-center gap-2">
                  <svg className={`h-4 w-4 ${passwordValidation.minLength ? 'text-red-600' : 'text-gray-400'}`} viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className={passwordValidation.minLength ? 'text-red-600 font-medium' : ''}>Legalább 6 karakter</span>
                </li>
                <li className="flex items-center gap-2">
                  <svg className={`h-4 w-4 ${passwordValidation.hasLower ? 'text-red-600' : 'text-gray-400'}`} viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className={passwordValidation.hasLower ? 'text-red-600 font-medium' : ''}>Legalább egy kisbetű</span>
                </li>
                <li className="flex items-center gap-2">
                  <svg className={`h-4 w-4 ${passwordValidation.hasUpper ? 'text-red-600' : 'text-gray-400'}`} viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className={passwordValidation.hasUpper ? 'text-red-600 font-medium' : ''}>Legalább egy nagybetű</span>
                </li>
                <li className="flex items-center gap-2">
                  <svg className={`h-4 w-4 ${passwordValidation.hasNumber ? 'text-red-600' : 'text-gray-400'}`} viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className={passwordValidation.hasNumber ? 'text-red-600 font-medium' : ''}>Legalább egy szám</span>
                </li>
              </ul>
            </div>
          </div>

          <div>
            <label htmlFor="passwordConfirm" className="block text-sm font-medium text-gray-700 mb-1">
              Jelszó megerősítése
            </label>
            <input
              id="passwordConfirm"
              type="password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="••••••••"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-red-600 text-white rounded-lg py-3 px-4 text-sm font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
          >
            {loading ? 'Regisztráció...' : 'Regisztráció'}
          </button>
        </form>

        <div className="text-center flex items-center justify-center gap-2">
          <p className="text-sm text-gray-600">
            Már van fiókod?
          </p>
          <p>
            <Link
              to="/login"
              className="text-red-600 hover:text-red-700 font-medium transition-colors duration-200"
            >
              Bejelentkezés
            </Link>
          </p>
        </div>
      </div>

      <div className="mt-8 text-center text-sm text-white z-10">
        <p>© {new Date().getFullYear()} SHS Gokart. Minden jog fenntartva.</p>
      </div>
    </div>
  );
};

export default Registration;

